import {
  MessagesSquare,
  Sparkles,
  MessageCircleQuestion,
  CreditCard,
  ListChecks,
  CalendarClock,
  Gauge,
  Info,
} from "lucide-react";

const CHANNELS = ["SMS", "WhatsApp", "Email", "Llamada automática"];

const AMBIGUOUS = [
  "“Creo que sí.”",
  "“Te aviso.”",
  "“Intentaré ir.”",
  "“Probablemente.”",
  "“Ya te confirmaré.”",
  "“No estoy seguro.”",
];

const AMBIGUOUS_FLOW = [
  "Clasifica la cita como no confirmada",
  "Envía una pregunta de aclaración",
  "Escala el caso a recepción si sigue sin confirmación clara",
  "Muestra una alerta en el dashboard",
];

const WAITLIST_STEPS = [
  "Identifica pacientes disponibles",
  "Filtra por tratamiento o tipo de cita",
  "Comprueba disponibilidad horaria",
  "Ofrece automáticamente el hueco",
  "Actualiza el calendario cuando un paciente acepta",
];

const RISK_FACTORS = [
  "Historial de cancelaciones",
  "Historial de no-shows",
  "Tiempo desde la reserva",
  "Tiempo hasta la cita",
  "Tipo de tratamiento",
  "Duración de la cita",
  "Valor estimado de la cita",
  "Canal y comportamiento de respuesta",
];

const MESSAGE_FACTORS = [
  "Tipo de paciente",
  "Procedimiento",
  "Duración de la cita",
  "Valor aproximado del tratamiento",
  "Antelación de la reserva",
  "Historial de cancelaciones",
  "Riesgo histórico de ausencia",
];

const CardShell = ({ icon: Icon, title, children, className = "", testId }) => (
  <article
    data-testid={testId}
    className={`rounded-xl border border-[#E8EDF5] bg-white p-6 shadow-sm transition-all duration-200 hover:shadow-md sm:p-7 ${className}`}
  >
    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy">
      <Icon className="h-5 w-5 text-white" strokeWidth={1.8} />
    </div>
    <h3 className="mt-4 font-heading text-lg font-bold text-navy">{title}</h3>
    {children}
  </article>
);

export default function Solution() {
  return (
    <section id="solucion" className="bg-platinum py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-navy-light">
            La solución
          </p>
          <h2
            data-testid="solution-title"
            className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-[2.6rem] lg:leading-[1.15]"
          >
            La solución con IA para reducir los no-shows.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
            Un sistema autónomo que confirma mejor, detecta riesgos antes y rescata los
            huecos disponibles.
          </p>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <CardShell icon={MessagesSquare} title="Confirmación multicanal" testId="solution-card-multicanal">
            <p className="mt-2 text-sm leading-relaxed text-steel">
              El sistema confirma las citas mediante:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {CHANNELS.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-[#DCE5F0] bg-platinum px-3 py-1 text-xs font-semibold text-navy"
                >
                  {c}
                </span>
              ))}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-steel">
              La clínica puede utilizar uno o varios canales según el perfil del paciente
              y sus preferencias.
            </p>
          </CardShell>

          <CardShell
            icon={Sparkles}
            title="Mensajes dinámicos"
            testId="solution-card-mensajes"
            className="lg:col-span-2"
          >
            <p className="mt-2 text-sm leading-relaxed text-steel">
              La IA adapta cada mensaje según:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {MESSAGE_FACTORS.map((f) => (
                <span
                  key={f}
                  className="rounded-full bg-platinum px-3 py-1 text-xs font-medium text-steel"
                >
                  {f}
                </span>
              ))}
            </div>
            <div className="mt-4 max-w-md rounded-2xl rounded-tl-sm border border-[#DDEAE2] bg-[#F2F9F5] p-4">
              <p className="text-sm leading-relaxed text-navy">
                “Hola, Marta. Te recordamos tu cita de implantología del jueves 14 a las
                16:30. La sesión tiene una duración aproximada de 90 minutos. Responde{" "}
                <strong>SÍ</strong> para confirmarla o <strong>CAMBIAR</strong> para ver
                otras opciones.”
              </p>
              <p className="mt-2 text-[11px] font-medium text-steel">
                Ejemplo de mensaje generado por la IA
              </p>
            </div>
          </CardShell>

          <CardShell
            icon={MessageCircleQuestion}
            title="Detección de respuestas ambiguas"
            testId="solution-card-ambiguas"
            className="lg:col-span-2"
          >
            <p className="mt-2 text-sm leading-relaxed text-steel">
              La IA detecta respuestas como:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {AMBIGUOUS.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-dashed border-navy/25 bg-white px-3 py-1 text-xs font-medium text-navy-light"
                >
                  {a}
                </span>
              ))}
            </div>
            <ol className="mt-4 space-y-2">
              {AMBIGUOUS_FLOW.map((step, i) => (
                <li key={step} className="flex items-start gap-3 text-sm text-navy">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </CardShell>

          <CardShell icon={CreditCard} title="Depósito o tarjeta de garantía" testId="solution-card-deposito">
            <p className="mt-2 text-sm leading-relaxed text-steel">
              Para citas de alto valor o pacientes con riesgo elevado, el sistema puede
              solicitar:
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-navy">
              {["Depósito", "Tarjeta de garantía", "Confirmación reforzada", "Aceptación de las condiciones de cancelación"].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-recover" />
                    {item}
                  </li>
                )
              )}
            </ul>
            <p className="mt-4 flex items-start gap-2 rounded-lg bg-platinum p-3 text-[11px] leading-relaxed text-steel">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-navy-light" />
              Esta función debe configurarse según las políticas de cada clínica y la
              normativa aplicable.
            </p>
          </CardShell>

          <CardShell icon={ListChecks} title="Lista de espera inteligente" testId="solution-card-lista-espera">
            <p className="mt-2 text-sm leading-relaxed text-steel">
              Cuando se produce una cancelación, la IA:
            </p>
            <ol className="mt-3 space-y-2">
              {WAITLIST_STEPS.map((step, i) => (
                <li key={step} className="flex items-start gap-3 text-sm text-navy">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy-light text-[10px] font-bold text-white">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </CardShell>

          <CardShell icon={CalendarClock} title="Reprogramación autónoma" testId="solution-card-reprogramacion">
            <p className="mt-2 text-sm leading-relaxed text-steel">
              Si el paciente no puede acudir, el sistema ofrece varias franjas
              alternativas:
            </p>
            <div className="mt-3 rounded-2xl rounded-tl-sm border border-[#DDEAE2] bg-[#F2F9F5] p-4">
              <p className="text-sm leading-relaxed text-navy">
                “Podemos reprogramar tu cita. Tenemos disponibles el martes a las 10:00,
                el miércoles a las 17:30 o el viernes a las 12:00. Responde con la opción
                que prefieras.”
              </p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-steel">
              La reprogramación queda reflejada en el calendario conectado.
            </p>
          </CardShell>

          <CardShell icon={Gauge} title="Predicción del riesgo de no-show" testId="solution-card-prediccion">
            <p className="mt-2 text-sm leading-relaxed text-steel">
              El sistema calcula el nivel de riesgo utilizando factores como:
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-1.5 text-sm text-navy sm:grid-cols-2">
              {RISK_FACTORS.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-navy-light" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex gap-2">
              <span className="rounded-full bg-[#E7F4EC] px-3 py-1 text-xs font-semibold text-recover">
                Riesgo bajo
              </span>
              <span className="rounded-full bg-[#E8EEF7] px-3 py-1 text-xs font-semibold text-navy-light">
                Riesgo medio
              </span>
              <span className="rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white">
                Riesgo alto
              </span>
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-steel">
              La predicción es una estimación que ayuda a priorizar la intervención de
              recepción; no es una certeza absoluta.
            </p>
          </CardShell>
        </div>
      </div>
    </section>
  );
}
